import React, { useState, useCallback, useEffect } from 'react';
import { HTMLEditor, sanitizeHTML } from './HTMLEditor';
import { PDFPreview, HTMLPreview } from './PDFPreview';
import { offersAPI } from '../utils/api';
import toast from 'react-hot-toast';
import {
  DownloadIcon,
  EyeIcon,
  SendIcon,
  SplitIcon,
} from 'lucide-react';

interface HTMLEditorContainerProps {
  initialContent?: string;
  offertaId?: string;
  onSave?: (html: string) => Promise<void>;
  onExportPDF?: () => void;
  onSendForSignature?: () => void;
  readOnly?: boolean;
}

type PreviewMode = 'html' | 'pdf';

export const HTMLEditorContainer: React.FC<HTMLEditorContainerProps> = ({
  initialContent = '',
  offertaId,
  onSave,
  onExportPDF,
  onSendForSignature,
  readOnly = false,
}) => {
  const [htmlContent, setHtmlContent] = useState(initialContent);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('html');
  const [pdfUrl, setPdfUrl] = useState<string | undefined>();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // Update content when initialContent changes
  useEffect(() => {
    if (initialContent && initialContent !== htmlContent) {
      setHtmlContent(initialContent);
    }
  }, [initialContent]);

  const handleContentChange = useCallback((html: string) => {
    setHtmlContent(html);
    // Reset PDF preview when content changes
    if (pdfUrl) {
      setPdfUrl(undefined);
    }
  }, [pdfUrl]);

  const handleSave = useCallback(async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      const sanitized = sanitizeHTML(htmlContent);
      await onSave(sanitized);
      toast.success('Contenuto salvato con successo');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Errore durante il salvataggio');
    } finally {
      setIsSaving(false);
    }
  }, [htmlContent, onSave]);

  const handleGeneratePDF = useCallback(async () => {
    if (!offertaId) {
      toast.error('Salva prima l\'offerta per generare il PDF');
      return;
    }

    setIsGeneratingPDF(true);
    setPdfError(undefined);

    try {
      // First save the HTML content
      if (onSave) {
        const sanitized = sanitizeHTML(htmlContent);
        await onSave(sanitized);
      }

      // Then generate PDF preview
      const response = await offersAPI.downloadPDF(offertaId, true);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      setPdfUrl(url);
      setPreviewMode('pdf');
      toast.success('Anteprima PDF generata');
    } catch (error: any) {
      console.error('PDF generation error:', error);
      const errorMessage = error.response?.data?.detail || 'Errore durante la generazione del PDF';
      setPdfError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [offertaId, htmlContent, onSave]);

  const handleDownloadPDF = useCallback(async () => {
    if (!offertaId) {
      toast.error('Salva prima l\'offerta per scaricare il PDF');
      return;
    }

    try {
      toast.loading('Generazione PDF in corso...', { id: 'pdf-download' });

      // Save HTML content first
      if (onSave) {
        const sanitized = sanitizeHTML(htmlContent);
        await onSave(sanitized);
      }

      // Generate and download PDF
      if (onExportPDF) {
        onExportPDF();
      } else {
        const response = await offersAPI.downloadPDF(offertaId, false);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `offerta_${offertaId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      toast.success('PDF scaricato con successo', { id: 'pdf-download' });
    } catch (error: any) {
      console.error('PDF download error:', error);
      toast.error(
        error.response?.data?.detail || 'Errore durante il download del PDF',
        { id: 'pdf-download' }
      );
    }
  }, [offertaId, htmlContent, onSave, onExportPDF]);

  const handleRetryPDF = useCallback(() => {
    setPdfError(undefined);
    handleGeneratePDF();
  }, [handleGeneratePDF]);

  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Toolbar */}
      <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <SplitIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">
            Editor HTML Documento
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {/* Preview Mode Toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setPreviewMode('html')}
              className={`px-3 py-2 text-sm font-medium ${
                previewMode === 'html'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              HTML
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode('pdf')}
              className={`px-3 py-2 text-sm font-medium ${
                previewMode === 'pdf'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              PDF
            </button>
          </div>

          {/* Action Buttons */}
          {!readOnly && (
            <>
              <button
                type="button"
                onClick={handleGeneratePDF}
                disabled={isGeneratingPDF || !offertaId}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                {isGeneratingPDF ? 'Generazione...' : 'Anteprima PDF'}
              </button>

              <button
                type="button"
                onClick={handleDownloadPDF}
                disabled={!offertaId}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DownloadIcon className="h-4 w-4 mr-2" />
                Scarica PDF
              </button>

              {onSendForSignature && (
                <button
                  type="button"
                  onClick={onSendForSignature}
                  disabled={!offertaId}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SendIcon className="h-4 w-4 mr-2" />
                  Invia per Firma
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Editor and Preview Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor Panel */}
        <div className="flex-1 border-r border-gray-200 overflow-auto">
          <HTMLEditor
            initialContent={htmlContent}
            onChange={handleContentChange}
            onSave={isSaving ? undefined : handleSave}
            readOnly={readOnly}
          />
        </div>

        {/* Preview Panel */}
        <div className="flex-1 overflow-auto bg-gray-100">
          {previewMode === 'html' ? (
            <HTMLPreview html={sanitizeHTML(htmlContent)} className="h-full" />
          ) : (
            <PDFPreview
              pdfUrl={pdfUrl}
              isLoading={isGeneratingPDF}
              error={pdfError}
              onRetry={handleRetryPDF}
            />
          )}
        </div>
      </div>
    </div>
  );
};
